import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@oxxean/types';
import {
  createLaborRelationsCase,
  deleteLaborRelationsCase,
  fetchLaborRelations,
  updateLaborRelationsCase,
} from '../../lib/api/manual-data';
import { queryKeys } from '../../lib/query-client';
import { errorMessage } from '../../lib/errors';
import { formatDate, toDateInputValue } from '../../lib/format';
import { useAuth } from '../../hooks/useAuth';
import { useCompanyAreas } from '../../hooks/useCompanyAreas';
import { useFilters } from '../../hooks/useFilters';
import { useTableState } from '../../hooks/useTableState';
import { useToasts } from '../../hooks/useToasts';
import type { CatalogOption, LaborRelationsCase } from '../../types/domain';
import { DataBoundary } from '../../components/DataBoundary';
import { DataTable, type Column } from '../../components/DataTable';
import { ExportMenu } from '../../components/ExportMenu';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { Button, Card, Notice, TableSkeleton } from '../../components/primitives';
import { SelectField, TextAreaField, TextField } from '../../components/form';
import { PageHeading } from '../layout/PageHeading';
import { CaseStatusBadge, CatalogOptions, RiskBadge, RiskOptions, StatusOptions } from './common';
import {
  LABOR_RELATIONS_DEFAULTS,
  laborRelationsFormSchema,
  toLaborRelationsInput,
  type LaborRelationsFormValues,
} from './schemas';

/**
 * Relaciones laborales (§19).
 *
 * Estos casos nacen en la plataforma: no vienen de BUK y NUNCA se pierden en una
 * resincronización (DEC-A03). Toda modificación queda en la auditoría.
 */
export function LaborRelationsPage(): JSX.Element {
  const { can } = useAuth();
  const { filters, options } = useFilters();
  const { push } = useToasts();
  const queryClient = useQueryClient();
  const table = useTableState({ sortBy: 'occurredOn', sortDir: 'desc' });

  const [editing, setEditing] = useState<LaborRelationsCase | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<LaborRelationsCase | null>(null);

  const canManage = can(PERMISSIONS.LABOR_RELATIONS_MANAGE);

  const listQuery = useQuery({
    queryKey: queryKeys.laborRelations({ filters, state: table.state }),
    queryFn: () => fetchLaborRelations(filters, table.state),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['labor-relations'] });
  };

  const saveMutation = useMutation({
    mutationFn: async (values: LaborRelationsFormValues) => {
      const payload = toLaborRelationsInput(values);
      return editing === null
        ? createLaborRelationsCase(payload)
        : updateLaborRelationsCase(editing.id, payload);
    },
    onSuccess: () => {
      push({
        kind: 'success',
        title: editing === null ? 'Caso registrado' : 'Caso actualizado',
        message: 'El cambio quedó registrado en la auditoría.',
      });
      setFormOpen(false);
      setEditing(null);
      invalidate();
    },
    onError: (error) => {
      push({ kind: 'error', title: 'No se pudo guardar el caso', message: errorMessage(error) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteLaborRelationsCase(id),
    onSuccess: () => {
      push({ kind: 'success', title: 'Caso eliminado' });
      setPendingDelete(null);
      invalidate();
    },
    onError: (error) => {
      push({ kind: 'error', title: 'No se pudo eliminar', message: errorMessage(error) });
    },
  });

  const columns = useMemo<Array<Column<LaborRelationsCase>>>(
    () => [
      {
        key: 'occurredOn',
        header: 'Fecha',
        sortable: true,
        render: (row) => formatDate(row.occurredOn),
        plain: (row) => row.occurredOn,
      },
      {
        key: 'companyLabel',
        header: 'Empresa',
        sortable: true,
        render: (row) => row.companyLabel,
        plain: (row) => row.companyLabel,
      },
      {
        key: 'areaLabel',
        header: 'Área',
        sortable: true,
        render: (row) => row.areaLabel,
        plain: (row) => row.areaLabel,
      },
      {
        key: 'caseType',
        header: 'Tipo de caso',
        sortable: true,
        render: (row) => row.caseType,
        plain: (row) => row.caseType,
      },
      {
        key: 'description',
        header: 'Descripción',
        render: (row) => (
          <span title={row.description}>
            {row.description.length > 90 ? `${row.description.slice(0, 90)}…` : row.description}
          </span>
        ),
        plain: (row) => row.description,
      },
      {
        key: 'risk',
        header: 'Riesgo',
        sortable: true,
        render: (row) => <RiskBadge risk={row.risk} />,
        plain: (row) => row.risk,
      },
      {
        key: 'responsible',
        header: 'Responsable',
        sortable: true,
        render: (row) => row.responsible,
        plain: (row) => row.responsible,
      },
      {
        key: 'dueDate',
        header: 'Fecha comprometida',
        sortable: true,
        render: (row) => formatDate(row.dueDate),
        plain: (row) => row.dueDate,
      },
      {
        key: 'status',
        header: 'Estado',
        sortable: true,
        render: (row) => <CaseStatusBadge status={row.status} />,
        plain: (row) => row.status,
      },
      {
        key: 'closedOn',
        header: 'Cierre',
        sortable: true,
        render: (row) => formatDate(row.closedOn),
        plain: (row) => row.closedOn,
      },
      {
        key: 'evidenceReference',
        header: 'Evidencia',
        render: (row) => row.evidenceReference ?? '—',
        plain: (row) => row.evidenceReference,
      },
    ],
    [],
  );

  const rows = listQuery.data;

  return (
    <>
      <PageHeading
        title="Relaciones laborales"
        description="Casos de relaciones laborales registrados en la plataforma. Se reportan dos medidas distintas: casos abiertos al cierre del período (stock) y casos ocurridos dentro del período (flujo)."
        actions={
          <>
            <ExportMenu moduleKey="labor-relations" />
            {canManage && (
              <Button
                variant="primary"
                icon="plus"
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              >
                Nuevo caso
              </Button>
            )}
          </>
        }
      />

      <Notice tone="info">
        <strong>Casos-mes no es casos.</strong> Un caso abierto durante tres meses cuenta una vez como
        caso y tres veces si se suman los stocks mensuales. La plataforma no acumula stocks a lo largo
        del tiempo.
      </Notice>

      <Card title="Casos registrados" flush>
        <DataBoundary
          isLoading={listQuery.isLoading}
          isError={listQuery.isError}
          error={listQuery.error}
          onRetry={() => void listQuery.refetch()}
          isEmpty={rows !== undefined && rows.total === 0 && table.state.search.length === 0}
          empty={{
            title: 'Sin casos registrados',
            message: canManage
              ? 'No hay casos para los filtros aplicados. Puede registrar el primero con «Nuevo caso».'
              : 'No hay casos para los filtros aplicados.',
          }}
          skeleton={<TableSkeleton columns={8} />}
        >
          {rows !== undefined && (
            <DataTable
              caption="Casos de relaciones laborales"
              columns={columns}
              rows={rows.data}
              getRowId={(row) => row.id}
              state={table.state}
              onStateChange={table.setState}
              total={rows.total}
              totalPages={rows.totalPages}
              searchPlaceholder="Buscar por tipo, responsable o descripción…"
              isFetching={listQuery.isFetching}
              rowActions={
                canManage
                  ? (row) => (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          icon="edit"
                          onClick={() => {
                            setEditing(row);
                            setFormOpen(true);
                          }}
                          aria-label={`Editar caso del ${formatDate(row.occurredOn)}`}
                        >
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          icon="trash"
                          onClick={() => setPendingDelete(row)}
                          aria-label={`Eliminar caso del ${formatDate(row.occurredOn)}`}
                        />
                      </>
                    )
                  : undefined
              }
            />
          )}
        </DataBoundary>
      </Card>

      {formOpen && (
        <LaborRelationsForm
          editing={editing}
          companies={options?.companies}
          areasByCompany={options?.areasByCompany}
          saving={saveMutation.isPending}
          onCancel={() => {
            setFormOpen(false);
            setEditing(null);
          }}
          onSubmit={(values) => saveMutation.mutate(values)}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Eliminar caso"
        destructive
        confirmLabel="Eliminar"
        loading={deleteMutation.isPending}
        message={
          <>
            Esta acción elimina el caso de{' '}
            <strong>{pendingDelete?.companyLabel ?? ''}</strong> del{' '}
            {formatDate(pendingDelete?.occurredOn)} y no se puede deshacer. La eliminación queda
            registrada en la auditoría.
          </>
        }
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete !== null) deleteMutation.mutate(pendingDelete.id);
        }}
      />
    </>
  );
}

interface FormProps {
  editing: LaborRelationsCase | null;
  companies: CatalogOption[] | undefined;
  /** Todas las áreas con su `companyId`: el formulario encadena por empresa. */
  areasByCompany: CatalogOption[] | undefined;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (values: LaborRelationsFormValues) => void;
}

function LaborRelationsForm({
  editing,
  companies,
  areasByCompany,
  saving,
  onCancel,
  onSubmit,
}: FormProps): JSX.Element {
  const defaults: LaborRelationsFormValues =
    editing === null
      ? LABOR_RELATIONS_DEFAULTS
      : {
          companyId: editing.companyId ?? '',
          areaId: editing.areaId,
          caseType: editing.caseType,
          occurredOn: toDateInputValue(editing.occurredOn),
          description: editing.description,
          risk: editing.risk,
          responsible: editing.responsible,
          action: editing.action,
          dueDate: toDateInputValue(editing.dueDate),
          status: editing.status,
          comment: editing.comment ?? '',
          evidenceReference: editing.evidenceReference ?? '',
          closedOn: toDateInputValue(editing.closedOn),
        };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<LaborRelationsFormValues>({
    resolver: zodResolver(laborRelationsFormSchema),
    defaultValues: defaults,
  });

  const { areas, disabled: areaDisabled, hint: areaHint } = useCompanyAreas({
    areasByCompany,
    companyId: watch('companyId'),
    areaId: watch('areaId'),
    onAreaCleared: () => setValue('areaId', ''),
  });

  return (
    <Modal
      open
      size="wide"
      title={editing === null ? 'Nuevo caso de relaciones laborales' : 'Editar caso'}
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={saving}
            onClick={() => void handleSubmit(onSubmit)()}
          >
            Guardar caso
          </Button>
        </>
      }
    >
      <form className="form-grid" onSubmit={(event) => void handleSubmit(onSubmit)(event)} noValidate>
        <SelectField label="Empresa" error={errors.companyId?.message} {...register('companyId')}>
          <CatalogOptions options={companies} placeholder="Seleccione…" />
        </SelectField>

        <SelectField
          label="Área"
          disabled={areaDisabled}
          hint={areaHint}
          error={errors.areaId?.message}
          {...register('areaId')}
        >
          <CatalogOptions
            options={areas}
            placeholder={areaDisabled ? 'Elija la empresa primero…' : 'Seleccione…'}
          />
        </SelectField>

        <TextField
          label="Tipo de caso"
          required
          hint="Por ejemplo: reclamo individual, mediación, negociación, amonestación."
          error={errors.caseType?.message}
          {...register('caseType')}
        />

        <TextField
          label="Fecha del caso"
          type="date"
          required
          error={errors.occurredOn?.message}
          {...register('occurredOn')}
        />

        <TextAreaField
          label="Descripción"
          required
          full
          hint="Describa el hecho sin incluir datos personales innecesarios."
          error={errors.description?.message}
          {...register('description')}
        />

        <SelectField label="Riesgo" required error={errors.risk?.message} {...register('risk')}>
          <RiskOptions />
        </SelectField>

        <TextField
          label="Responsable"
          required
          error={errors.responsible?.message}
          {...register('responsible')}
        />

        <TextAreaField
          label="Acción comprometida"
          required
          full
          error={errors.action?.message}
          {...register('action')}
        />

        <TextField
          label="Fecha comprometida"
          type="date"
          hint="Plazo acordado para completar la acción."
          error={errors.dueDate?.message}
          {...register('dueDate')}
        />

        <SelectField label="Estado" required error={errors.status?.message} {...register('status')}>
          <StatusOptions />
        </SelectField>

        <TextField
          label="Fecha de cierre"
          type="date"
          hint="Obligatoria cuando el estado es «Cerrado»."
          error={errors.closedOn?.message}
          {...register('closedOn')}
        />

        <TextField
          label="Evidencia"
          hint="Referencia del respaldo: acta, correo, expediente."
          error={errors.evidenceReference?.message}
          {...register('evidenceReference')}
        />

        <TextAreaField
          label="Comentario"
          full
          error={errors.comment?.message}
          {...register('comment')}
        />
      </form>
    </Modal>
  );
}
